import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit
} from '@angular/core';
import { locale } from 'app/charts-dashboard/components/dialogs/compare-symbol-dialog/compare-symbol-dialog.locale';
import {
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { Instrument } from '@iv/model/instrument';
import {
  ChartFilterPatternNameEnum,
  ChartType,
  ChartTypeEnum,
  CustomControlsNameEnum,
  DateType,
  IChangeIVDiffStockSeries,
  IChangeStockSeries,
  IChartFilter,
  IChartFilterPattern,
  ICompareDateDialog,
  ICompareDialogOutput,
  ICompareSymbol,
  IOptionInfoStrikes,
  ISelectedFilters,
  Pattern,
  SymbolDialogActionEnum
} from 'app/charts-dashboard/interfaces';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { TuiDialogContext } from '@taiga-ui/core';
import { ChartsDashboardCardQuery } from '../../../state/card';
import { dropdownTemplateField } from '../../../helpers/charts-module-helpers';
import { ChartsHttpService } from 'app/charts-dashboard/services/charts-http.service';
import { addSingleContractAnalyzerExpirations, addSingleContractAnalyzerStrikes } from 'app/charts-dashboard/state/data-state.mock';
import { switchMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'iv-live-compare-symbol-dialog',
  templateUrl: './compare-symbol-dialog.component.html',
  styleUrls: ['./compare-symbol-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompareSymbolDialogComponent implements OnInit {

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<ICompareDialogOutput, (IChangeStockSeries & ICompareSymbol & ICompareDateDialog & IChangeIVDiffStockSeries)>,
    private readonly cardQuery: ChartsDashboardCardQuery,
    private readonly cd: ChangeDetectorRef,
    private readonly chartsHttpService: ChartsHttpService
  ) {
  }

  public chartTypeEnum = ChartTypeEnum;

  public get cardId(): ChartType {
    return this.cardQuery.getEntity(this.context.data.parentCardId).chartId;
  }

  public get listStocks(): Instrument[] {
    return this.context.data.instruments;
  }

  public get isCompare(): boolean {
    return this.context.data.action === SymbolDialogActionEnum.COMPARE_SERIES;
  }

  public get isUpdateSymbols(): boolean {
    return this.context.data.action === SymbolDialogActionEnum.CHANGE_STOCK_IN_INTRADAY_CHART;
  }

  public get mainSeriesSelectedFilters(): {
    selected: ISelectedFilters,
    filters: IChartFilter[]
  } {
    return {selected: this.context.data.selectedFilters, filters: this.context.data.filters};
  }

  public locale = locale;
  public filterTypes = ChartFilterPatternNameEnum;
  public selectedStock: Instrument;
  public filterPattern: { [patternId: string]: IChartFilterPattern<Pattern>[] } = {};
  public isFullScreenOpened = !!document.fullscreenElement;
  public controlAction: string = ''
  public noData: boolean = false;
  public errorText: string = 'There is no data for selected stock'
  public symbolSub: Subscription = new Subscription();

  public form = new FormGroup({
    stockId: new FormControl(null, Validators.required),
    filters: new FormGroup({})
  });

  public get filters(): FormGroup {
    return this.form.get('filters') as FormGroup;
  }

  public ngOnInit() {
    if (this.isCompare || this.isUpdateSymbols) {

      this.setConfigurationForm();

      if (this.cardId === ChartTypeEnum.IV_DIFFERENCE) {
        this.form.removeControl(ChartFilterPatternNameEnum.STOCK_ID);
        this.filters.get(CustomControlsNameEnum.STOCK_ID_FIRST).setValue(null);
        this.filters.get(CustomControlsNameEnum.STOCK_ID_SECOND).setValue(null);
      }
    }
    if (this.cardId === ChartTypeEnum.SINGLE_CONTRACT_ANALYZER) {
      this.configureSingleContractAnalyzer()
    }

  }

  public ngOnDestroy(): void {
    this.symbolSub?.unsubscribe();
  }

  public autocompleteItemClick(event: Instrument) {
    this.selectedStock = event;
  }

  public updateFormValidity(event: { value: string, previousValue: string }, control: FormControl): void {
    if (!event.value) {
      control.setValue(null);
      this.noData = false;
      this.cd.detectChanges()
    }

    if (event.value !== this.selectedStock?.nameAndExchange && this.cardId !== ChartTypeEnum.IV_DIFFERENCE) {
      this.form.setErrors({differencesWithSelectedStock: true});
    }
  }

  public getFilterName(filterKey: string): string {
    return this.mainSeriesSelectedFilters.filters.find((chartFilter: IChartFilter) => chartFilter.parameterId === filterKey).name;
  }

  public dropdownTemplateField(controlName: string): { list: string, input: string } {
    return dropdownTemplateField(controlName);
  }

  public submit(): void {
    if (this.context.data.currentPoint) {
      this.context.data.selectedFilters = {
        ...this.context.data.selectedFilters,
        [`stockId-${this.context.data.currentPoint}`]: this.selectedStock
      };
    }

    if (this.cardId === ChartTypeEnum.SINGLE_CONTRACT_ANALYZER) {
      const filters = this.getFiltersForSumbit()
      this.context.completeWith({
        action: this.context.data.action,
        stock: this.selectedStock,
        selectedFilters: {
          ...this.context.data.selectedFilters,
          ...this.filters.value,
          // Нужно для запроса на сервер, можно переделать в this.filters.value, но сам контролл TIME_RANGE скрыть в темлейте (это лучший вариант)
          timeRange: this.mainSeriesSelectedFilters.selected[ChartFilterPatternNameEnum.TIME_RANGE],
          eodTimeRange: this.mainSeriesSelectedFilters.selected[ChartFilterPatternNameEnum.EOD_TIME_RANGE],
        },
        filters,
        seriesId: this.context.data.seriesId,
        parentCardId: this.context.data.parentCardId
      });
    }

    this.context.completeWith({
      action: this.context.data.action,
      stock: this.selectedStock,
      selectedFilters: {
        ...this.context.data.selectedFilters,
        ...this.filters.value,
        // Нужно для запроса на сервер, можно переделать в this.filters.value, но сам контролл TIME_RANGE скрыть в темлейте (это лучший вариант)
        timeRange: this.mainSeriesSelectedFilters.selected[ChartFilterPatternNameEnum.TIME_RANGE],
        eodTimeRange: this.mainSeriesSelectedFilters.selected[ChartFilterPatternNameEnum.EOD_TIME_RANGE],
      },
      seriesId: this.context.data.seriesId,
      parentCardId: this.context.data.parentCardId
    });
  }

  private setConfigurationForm(): void {
    this.mainSeriesSelectedFilters.filters.forEach((chartFilter: IChartFilter) => {

      if (
        chartFilter.parameterId === ChartFilterPatternNameEnum.STOCK_ID ||
        chartFilter.parameterId === ChartFilterPatternNameEnum.TIME_RANGE ||
        chartFilter.parameterId === ChartFilterPatternNameEnum.EOD_TIME_RANGE ||
        chartFilter.parameterId === ChartFilterPatternNameEnum.REALTIME
      ) return;

      this.filterPattern[chartFilter.parameterId] = chartFilter.pattern;
      this.filters.addControl(chartFilter.parameterId, new FormControl(this.mainSeriesSelectedFilters.selected[chartFilter.parameterId], Validators.required));
    });
  }

  private onSymbolValuesChanges(): void {
    this.symbolSub = this.form.get('stockId').valueChanges.subscribe((changes) => {
      if (changes?.stockId) {
        this.intraDayOptionControls(changes)
      }
    })
  }

  private intraDayOptionControls(stock: Instrument): void {
    this.chartsHttpService.getOptInfoExpirations(stock.stockId)
      .subscribe((expirations: string[]) => {
        if (!expirations.length) {
          this.errorText = 'There is no data for selected stock'
          this.noData = true;
          this.setErrorToSymbolControl()
          return
        }
        this.noData = false;
        this.setExpirationsControl(addSingleContractAnalyzerExpirations(expirations));
        this.filters.get(CustomControlsNameEnum.EXPIRATION_DATE)
          .valueChanges
          .pipe(
            switchMap((value: IChartFilterPattern<DateType>) => {
              return this.chartsHttpService.getOptInfoStrikes(
                {
                  stockId: this.form.get(ChartFilterPatternNameEnum.STOCK_ID).value.stockId,
                  expirationDate: value?.name
                }
              );
            }),
          )
          .subscribe((strikes: IOptionInfoStrikes) => {
            this.setStrikesControl([...addSingleContractAnalyzerStrikes(strikes)],{description: strikes.center.toString(), name: strikes.center})
          });
      });
  }

  private setExpirationsControl(expirations: any): void {
    if( expirations?.length ) {
      this.filters.get(CustomControlsNameEnum.EXPIRATION_DATE).enable()
      this.filters.get(CustomControlsNameEnum.EXPIRATION_DATE).setValue(expirations[0]?.pattern[0])
      this.filterPattern[CustomControlsNameEnum.EXPIRATION_DATE] = expirations[0]?.pattern
      this.chartsHttpService
        .getOptInfoStrikes({
          stockId: this.form.get(ChartFilterPatternNameEnum.STOCK_ID).value
            .stockId,
          expirationDate: expirations[0]?.pattern[0]?.name,
        })
        .subscribe((strikes: IOptionInfoStrikes) => {
          this.setStrikesControl([...addSingleContractAnalyzerStrikes(strikes)],{description: strikes.center.toString(), name: strikes.center})
        });
      this.cd.detectChanges()
    }
  }

  private setStrikesControl(strikes:IChartFilter[] = [], defaultValue:IChartFilterPattern<number>): void {
    this.filters.get(CustomControlsNameEnum.STRIKE).enable()
    this.filterPattern[CustomControlsNameEnum.STRIKE] = strikes[0].pattern;
    this.filters.get(CustomControlsNameEnum.STRIKE).setValue(defaultValue);
    this.cd.detectChanges()
  }

  private configureSingleContractAnalyzer(): void {
    this.filters.get(CustomControlsNameEnum.EXPIRATION_DATE)?.disable()
    this.filters.get(CustomControlsNameEnum.STRIKE)?.disable()
    this.onSymbolValuesChanges()
  }

  private getFiltersForSumbit(): IChartFilter[] {
    const filters = JSON.parse(JSON.stringify(this.mainSeriesSelectedFilters.filters))

    for (let key in this.filterPattern) {
      for (let index in filters) {
        if (filters[index].parameterId === key) {
            filters[index].pattern = this.filterPattern[key]
        }
      }
    }
    return filters
  }

  private setErrorToSymbolControl(): void {
    this.form.controls['stockId'].setErrors({'incorrect': true})
    this.cd.detectChanges()
  }

  public onStrikeChanged(message: string): void {
    if (message === 'valid') {
      this.noData = false
      this.filters.controls['strike'].setErrors(null)
    } else {
      this.filters.controls['strike'].setErrors({'incorrect': true})
      this.errorText = message
      this.noData = true
    }
  }
}
